/* eslint-disable react/jsx-no-comment-textnodes */
import type { ReactNode, ReactElement } from 'react';

import { Icon } from '@iconify/react';
import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { checkRoute } from 'src/utils/permissions';

export interface SidebarItemProps {
  level?: number;
  title?: string;
  href?: string;
  badge?: (...args: any[]) => any;
  info?: ReactElement;
  icon?: ReactElement;
  open?: boolean;
  alsoActiveOn?: string[];
  children?: ReactNode;
  [key: string]: any;
}

// ----------------------------------------------------------------------

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  // Protect sidebar styling from external interference
  '& .MuiListItemIcon-root': {
    color: 'inherit !important',
    minWidth: '36px !important',
    '& svg, & .MuiSvgIcon-root': {
      color: 'inherit !important',
      fill: 'currentColor !important',
    },
  },
  '& .MuiListItemText-root': {
    marginLeft: theme.spacing(1),
  },
  '&.isActiveRoot': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.primary.main} !important`,
      '& svg, & .MuiSvgIcon-root': {
        color: `${theme.palette.primary.main} !important`,
        fill: `${theme.palette.primary.main} !important`,
      },
    },
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      position: 'absolute',
      backgroundColor: theme.palette.primary.main,
    },
  },
  '&.isActiveSub': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.text.primary} !important`,
      '& svg, & .MuiSvgIcon-root': {
        color: `${theme.palette.text.primary} !important`,
        fill: `${theme.palette.text.primary} !important`,
      },
    },
    '& .subIcon:before': {
      transform: 'scale(2)',
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

const SubIconStyle = styled('span')(({ theme }) => ({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:before': {
    width: 4,
    height: 4,
    content: "''",
    display: 'block',
    borderRadius: '50%',
    backgroundColor: theme.palette.text.disabled,
    transition: theme.transitions.create('transform'),
  },
}));

const NavLinkAdapter = React.forwardRef(function LinkBehavior(
  // @ts-ignore legacy boundary
  { button: _buttonProp, ...props },
  ref
) {
  return <RouterLink ref={ref} {...props} />;
});

// ----------------------------------------------------------------------

export default function SidebarItem({
  level,
  user,
  title,
  href,
  badge: Badge = React.Fragment,
  info,
  icon,
  open = false,
  crossOrigin = false,
  // Other paths that should highlight this item, e.g. a page's other tabs.
  alsoActiveOn = [],
  children,
}) {
  const [show, setShow] = useState(open);
  const isSubItem = level > 0;

  if (!checkRoute(user, href)) return <></>;

  const handleShow = () => {
    setShow((show) => !show);
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleShow}
          className={open ? 'isActiveRoot' : ''}
        >
          <ListItemIcon>
            <Badge>{icon}</Badge>
          </ListItemIcon>
          <ListItemText disableTypography primary={title} />
          {info}
          <Box
            component={Icon}
            icon={show ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={show}>{children}</Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      // @ts-ignore legacy boundary
      to={crossOrigin ? null : href}
      onClick={(e) => {
        if (!crossOrigin) {
          return;
        }

        e.preventDefault();
        window.open(href, '_blank');
      }}
      component={!crossOrigin ? NavLinkAdapter : undefined}
      {...(!crossOrigin && {
        exact: open,
        activeClassName: isSubItem ? 'isActiveSub' : 'isActiveRoot',
        isActive: (match, location) => {
          const { pathname } = location;
          if (alsoActiveOn.includes(pathname)) {
            return true;
          }
          if (!match) {
            return false;
          }

          return match.url === pathname;
        },
      })}
    >
      <Badge>
        <ListItemIcon>
          {isSubItem ? <SubIconStyle className="subIcon" /> : icon}
        </ListItemIcon>
        <ListItemText disableTypography primary={title} />

        {info}
      </Badge>
    </ListItemStyle>
  );
}
